import { useEffect } from 'react'
import { create } from 'zustand'

export type GetAccessTokenOptions = { bypassCache?: boolean }
export type GetAccessTokenWithOptions = (options?: GetAccessTokenOptions) => Promise<string>
type GetAccessTokenFn = () => Promise<string>

export type AuthState = {
  hasSetAccessToken: boolean
  getAccessToken: GetAccessTokenWithOptions
  setGetAccessToken: (getAccessToken: GetAccessTokenFn) => unknown
}

export const useAuthStore = create<AuthState>()((set) => {
  // Local memory cache for token
  // Keep tokens cached for 1 minute
  let tokenCache: { token: string; expiresAt: number } | null = null
  const ttlMs = 60_000

  async function cachedGetAccessToken(
    getAccessTokenFn: GetAccessTokenFn,
    opts?: GetAccessTokenOptions
  ) {
    const now = Date.now()

    // Skip cache if bypass requested
    if (!opts?.bypassCache && tokenCache && tokenCache.expiresAt > now) {
      return tokenCache.token
    }

    const token = await getAccessTokenFn()

    // Never cache an empty/falsy token. Caching '' would serve `Bearer ` for the full TTL
    // after a transient failure (e.g. right after a reconnect), and consumers do not retry
    // the resulting 401s. Leaving the cache untouched lets the next call fetch a real token.
    if (token) {
      tokenCache = {
        token,
        expiresAt: now + ttlMs,
      }
    }

    return token
  }

  async function setGetAccessToken(getAccessTokenFn: GetAccessTokenWithOptions) {
    set({
      getAccessToken: (opts) => cachedGetAccessToken(getAccessTokenFn, opts),
      hasSetAccessToken: true,
    })
  }

  return {
    hasSetAccessToken: false,
    getAccessToken: () => Promise.resolve(''),
    setGetAccessToken,
  }
})

export function useCheckIfAccessTokenSet() {
  return useAuthStore((state) => state.hasSetAccessToken)
}

export function useSyncGetAccessTokenToStore({
  getAccessToken,
  isAuthenticated,
}: {
  getAccessToken: GetAccessTokenWithOptions
  isAuthenticated: boolean
}) {
  const hasSetAccessToken = useAuthStore((state) => state.hasSetAccessToken)

  useEffect(() => {
    if (!isAuthenticated) return
    useAuthStore.getState().setGetAccessToken(getAccessToken)
  }, [isAuthenticated, getAccessToken])

  // useEffect(() => {
  //   if (!user) return

  //   async function logUserAndToken() {
  //     log.info('user', user)
  //     const token = await getAccessTokenSilently()
  //     const decodedToken = jwtDecode(token)
  //     log.info('token', { token, decodedToken })
  //   }

  //   logUserAndToken()
  // }, [getAccessTokenSilently, user])

  return { hasSetAccessToken }
}

export const getAccessToken: AuthState['getAccessToken'] = (options) =>
  useAuthStore.getState().getAccessToken(options)

export const getHasSetAccessToken = () => useAuthStore.getState().hasSetAccessToken
